// @ts-ignore
import type Sodium from 'libsodium-wrappers-sumo';

// libsoudium global
declare global {
  interface Window {
    sodium?: typeof Sodium;
  }
}

let _sodium: typeof Sodium | null = null;

async function getSodium(): Promise<typeof Sodium> {
  if (_sodium) return _sodium;
  if (!window.sodium) {
    throw new Error('libsodium not loaded. Check index.html.');
  }
  await window.sodium.ready;
  _sodium = window.sodium;
  return _sodium;
}

const dec = new TextDecoder();

export function importSeedFromPem(privateKeyPem: string): Uint8Array {
  // Extract base64 from PEM
  const base64 = privateKeyPem
    .split(/\r?\n/)
    .filter(line => !line.startsWith('-----'))
    .join('');

  const raw = Uint8Array.from(atob(base64), c => c.charCodeAt(0));

  // SDK 'parseKey' logic
  // Acceptable sizes: exactly 32, 64, or >32 and <64
  let seed: Uint8Array;

  if (raw.length === 32) {
    seed = raw;
  } else if (raw.length === 64) {
    seed = raw.slice(0, 32);
  } else if (raw.length > 32 && raw.length < 64) {
    // Drop prefix so final 32 remain
    seed = raw.slice(raw.length % 32);
  } else {
    throw new Error(`Unexpected Ed25519 private key length: ${raw.length}`);
  }

  if (seed.length !== 32) {
    throw new Error(`Failed to parse Ed25519 seed; got ${seed.length} bytes`);
  }

  return seed;
}

export async function encrypt(
  publicKeyHex: string,
  message: string
): Promise<Uint8Array> {
  const s = await getSodium();

  const edPub = s.from_hex(publicKeyHex) as Uint8Array;
  if (edPub.length !== 32) {
    throw new Error('Ed25519 public key must be 32 bytes');
  }

  const xPub = s.crypto_sign_ed25519_pk_to_curve25519(edPub) as Uint8Array;
  return s.crypto_box_seal(message, xPub) as Uint8Array;
}

export async function decryptWithSeed(
  seed32: Uint8Array,
  encryptedBytes: Uint8Array
): Promise<string> {
  const s = await getSodium();

  if (seed32.length !== 32) {
    throw new Error('Ed25519 seed must be 32 bytes');
  }

  const kp = s.crypto_sign_seed_keypair(seed32) as {
    publicKey: Uint8Array;
    privateKey: Uint8Array;
  };

  const xSk = s.crypto_sign_ed25519_sk_to_curve25519(kp.privateKey);
  const xPk = s.crypto_sign_ed25519_pk_to_curve25519(kp.publicKey);

  const pt = s.crypto_box_seal_open(encryptedBytes, xPk, xSk);
  if (!pt) throw new Error('Decryption failed');

  return dec.decode(pt);
}

export async function decryptWithPem(
  privateKeyPem: string,
  encryptedBytes: Uint8Array
): Promise<string> {
  const seed = importSeedFromPem(privateKeyPem);
  return decryptWithSeed(seed, encryptedBytes);
}

export const ed25519 = { encrypt, importSeedFromPem, decryptWithSeed, decryptWithPem }
